import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';

import VisionAireLogo from '@/components/VisionAireLogo';

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const user = await login(email, password);
            navigate(user.role === 'admin' ? '/admin' : '/dashboard');
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail) {
                setError(formatApiError(detail));
            } else if (err.message === 'Network Error') {
                setError('Unable to connect to server. Please check your connection.');
            } else {
                setError(err.message || 'Something went wrong. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-dark-gradient" data-testid="login-page">
            <Navbar variant="dark" />
            <div className="min-h-screen flex items-center justify-center px-4 pt-16">
                <div className="w-full max-w-md">
                    <div className="card-dark rounded-2xl p-8">
                        <div className="flex items-center justify-center gap-2 mb-8">
                            <VisionAireLogo size={56} />
                        </div>
                        <h1 className="text-2xl font-medium text-white text-center mb-2 font-['Outfit']">Welcome Back</h1>
                        <p className="text-sm text-zinc-400 text-center mb-8">Sign in to your VisionAire Realty Services account</p>

                        {error && (
                            <div data-testid="login-error" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <Label className="text-zinc-300 text-sm mb-1.5 block">Email</Label>
                                <Input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                                    required placeholder="you@example.com"
                                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:ring-emerald-500/20" />
                            </div>
                            <div>
                                <Label className="text-zinc-300 text-sm mb-1.5 block">Password</Label>
                                <div className="relative">
                                    <Input data-testid="login-password" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                                        required placeholder="Enter your password"
                                        className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:ring-emerald-500/20 pr-10" />
                                    <button type="button" data-testid="login-toggle-password"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors">
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-emerald-400 transition-colors" data-testid="forgot-password-link">
                                    Forgot password?
                                </Link>
                            </div>
                            <Button data-testid="login-submit" type="submit" disabled={loading}
                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-5 font-semibold">
                                {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
                            </Button>
                        </form>

                        <p className="text-sm text-zinc-400 text-center mt-6">
                            Don't have an account?{' '}
                            <Link to="/register" className="text-emerald-400 hover:text-emerald-300 font-medium" data-testid="login-register-link">
                                Create one
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
